import { LUDUM_ERROR_CODES, LUDUM_USER_STATUSES } from '@/constants';
const { NO_CLUBS, SUBSCRIPTION_EXPIRED } = LUDUM_ERROR_CODES;
const { APPROVED, PENDING, INVITED } = LUDUM_USER_STATUSES;
import { ROUTES_HOME } from '@/constants/routes';
import { IS_PRODUCTION } from '@/constants/global';
import { isAthlete, isCoach, isManager } from '@/helpers/rolesHelper';
import { isPaddlingSession, isWaterSession } from '@/helpers/sessionHelper';
import router from '@/router';
import { i18n } from '@/lang';
import { db, fb } from '@/firebase-config';
import { getAuth, signInWithEmailAndPassword } from 'firebase/auth';
import { onSnapshot, getDoc, doc } from 'firebase/firestore';
import get from 'get-object-path';
import {DateTime} from "luxon";

export const user = {
    state: {
        subscribed: false,
        user: {},
        clubs: [],
        errorCode: 0,
        selectedClubId: null,
        userUpdates: {},
        firebaseEnabled: false,
        lastJSUpdate: null
    },

    getters: {
        clubId(state, getters) {
            return (
                state.selectedClubId ||
                Number(Vue.cookie.get('selectedClub')) ||
                get(getters, 'userActiveClubs[0].club.data.id')
            );
        },

        selectedClub(state, getters) {
            return state.clubs.find(({ club_id }) => club_id === getters.clubId);
        },

        selectedClubId(state, getters) {
            return (getters.selectedClub || {}).club_id;
        },

        isSelectedClubIndividual(state, getters) {
            return get(getters.selectedClub, 'club.data.is_individual');
        },

        selectedClubDefaultSportId(state, getters) {
            return get(getters.selectedClub, 'club.data.default_sport.data.id');
        },

        selectedClubGmtRowing(state, getters) {
            return get(getters.selectedClub, 'club.data.gmt_distribution.data') || [];
        },

        selectedClubGmtRowingSegmented(state, getters) {
            let gmtDistribution = getters.selectedClubGmtRowing;
            gmtDistribution.forEach(zone => {
                zone.end = zone.end != undefined ? zone.end + 1 : zone.end;
                if(zone.id <= 3)
                {
                    zone.subzones = [];

                    let firstSubzone = _.cloneDeep(zone);
                    firstSubzone.end = (firstSubzone.start + firstSubzone.end) / 2;

                    let secondSubzone = _.cloneDeep(zone);
                    secondSubzone.name = firstSubzone.name+'-5';
                    secondSubzone.start = firstSubzone.end;

                    zone.subzones.push(firstSubzone);
                    zone.subzones.push(secondSubzone);
                }
            });

            return gmtDistribution;
        },

        selectedClubGmtPaddling(state, getters) {
            return get(getters.selectedClub, 'club.data.paddle_gmt_distribution.data') || [];
        },

        selectedClubGmtPaddlingSegmented(state, getters) {
            let gmtDistribution = getters.selectedClubGmtPaddling;
            gmtDistribution.forEach(zone => {
                zone.end = zone.end != undefined ? zone.end + 1 : zone.end;
                if(zone.id <= 3)
                {
                    zone.subzones = [];

                    let firstSubzone = _.cloneDeep(zone);
                    firstSubzone.end = (firstSubzone.start + firstSubzone.end) / 2;

                    let secondSubzone = _.cloneDeep(zone);
                    secondSubzone.name = firstSubzone.name+'-5';
                    secondSubzone.start = firstSubzone.end;

                    zone.subzones.push(firstSubzone);
                    zone.subzones.push(secondSubzone);
                }
            });

            return gmtDistribution;
        },

        isClubDefaultSportWater(state, getters) {
            return isWaterSession(getters.selectedClubDefaultSportId);
        },

        isClubDefaultSportPaddling(state, getters) {
            return isPaddlingSession(getters.selectedClubDefaultSportId);
        },

        hasBillingPermissionInSelectedClub(state, getters) {
            const permissions = get(getters.selectedClub, 'permissions.data') || [];
            return !!permissions.find(permission => get(permission, 'permission.data.id') === 'manage-licences');
        },

        clubRoles(store, getters) {
            return get(getters.selectedClub, 'roles.data') || [];
        },

        isCurrentUserCoachOfSelectedClub(store, getters) {
            return getters.clubRoles.some(({ id }) => isCoach(id));
        },

        isCurrentUserAthleteOfSelectedClub(store, getters) {
            return getters.clubRoles.some(({ id }) => isAthlete(id));
        },

        isCurrentUserClubManagerOfSelectedClub(store, getters) {
            return getters.clubRoles.some(({ id }) => isManager(id));
        },

        selectedClubName(state, getters) {
            return get(getters.selectedClub, 'club.data.name');
        },

        userIndividualClub(state, getters) {
            return getters.userActiveClubs.find(club => get(club, 'club.data.is_individual'));
        },

        userActiveClubs(state) {
            return state.clubs.filter(club => get(club, 'status.data.id') === APPROVED);
        },

        userPendingClubs(state) {
            return state.clubs.filter(club => get(club, 'status.data.id') === PENDING);
        },

        userInvitedClubs(state) {
            return state.clubs.filter(club => get(club, 'status.data.id') === INVITED);
        },
        subscriptionHasExpired(state) {
            return state.errorCode === SUBSCRIPTION_EXPIRED;
        },
        userHasNoClubs(state) {
            return state.errorCode === NO_CLUBS;
        },
        userAllRoles(state) {
            return [...new Set(state.clubs.map(club => get(club, 'roles.data[0].name')))].join(', ');
        },
        firebaseEnabled(state) {
            return state.firebaseEnabled;
        },
        userUpdates(state) {
            return state.userUpdates;
        },
        usersClubsWithActiveSubscription(state) {
            return state.clubs.filter(club => get(club, 'status.data.id') === APPROVED && DateTime.fromISO(get(club, 'club.data.subscription_info.data.next_bill_date')) >= DateTime.now().startOf('day'));
        },
    },

    mutations: {
        setSelectedClubId(state, payload) {
            state.selectedClubId = payload;
        },

        setUserUpdates(state, data) {
            state.userUpdates = data;
        },

        updateFirebaseStatus(state, data) {
            state.firebaseEnabled = data;
        },

        userClubs(state, data) {
            state.clubs = _.sortBy(data, [o => o.club.data.name]);
        },

        updateErrorCode(state, data) {
            state.errorCode = data;
        },
        updateJSTimestamp(state, timestamp) {
            state.lastJSUpdate = timestamp;
        }
    },

    actions: {
        emptyClubs(context) {
            context.commit('userClubs', []);
        },

        updateErrorCode(context, value) {
            context.commit('updateErrorCode', value);
        },

        fetchUserClubs(context, forceLoad = false) {
            if (context.state.clubs.length > 0 && !forceLoad) {
                return;
            }
            return context
                .dispatch('get', {
                    url: api + 'my-clubs',
                    params: {
                        include:
                            'club,club.venue,' +
                            'club.creator:fields(email),' +
                            'club.subscription_info.subscription_plan.features,' +
                            'roles,permissions.permission:fields(id|name)'
                    }
                })
                .then(response => {
                    context.commit('userClubs', response.data);
                    context.dispatch('ensureClubIsSelected');
                    return response.data;
                });
        },

        ensureClubIsSelected(context) {
            const savedState = JSON.parse(localStorage.getItem('ludumGoToState'));
            const paramClubId = get(savedState, 'params.clubId') || 0;
            const storedClub = context.getters.userActiveClubs.find(({ club_id }) => club_id === Number(paramClubId));

            if (storedClub) return context.dispatch('selectClub', storedClub.club.data);

            const clubId = Vue.cookie.get('selectedClub') && parseInt(Vue.cookie.get('selectedClub'));

            if (!clubId || !context.getters.userActiveClubs.find(({ club_id }) => club_id === clubId)) {
                const club = context.getters.userIndividualClub
                    ? get(context.getters, 'userIndividualClub.club.data')
                    : get(context.getters, 'usersClubsWithActiveSubscription[0].club.data');
                club && context.dispatch('selectClub', club);
            }
        },

        selectClub(context, payload) {
            Vue.cookie.set('selectedClub', payload.id, { expires: '30D' });
            context.commit('setSelectedClubId', payload.id);

            const savedState = JSON.parse(localStorage.getItem('ludumGoToState'));
            savedState && localStorage.removeItem('ludumGoToState');

            context.commit('reportsGlobal/removeSquads');
            context.commit('resetCalendarState');

            const goToState = savedState || { name: ROUTES_HOME };
            router.push(goToState).catch(() => {});
        },

        checkToken(context) {
            return context
                .dispatch('get', {
                    url: `${api}validate-token?club_id=${get(context.getters, 'selectedClub.club_id')}`
                })
                .then(response => response)
                .catch(error => error);
        },

        async getFirebaseUserData(context, uid) {
            return doc(db, 'users', uid);
        },

        processFirebaseUpdates(context, argUser) {
            const userUpdates = context.getters.userUpdates;
            const user = argUser.data();
            if (user.clubs_updated_at > userUpdates.clubs_updated_at) {
                context.dispatch('fetchUserClubs', true);
            }
            if (user.user_updated_at > userUpdates.user_updated_at) {
                context.dispatch('clubHelper/getMe');
            }
            if (user.notifications_updated_at > userUpdates.notifications_updated_at) {
                context.dispatch('clubHelper/getNumberOfUnreadNotifications');
            }
            context.commit('setUserUpdates', user);
        },

        async subscribeToUpdates(context, payload) {
            if (this.subscribed || !IS_PRODUCTION) {
                return;
            }
            const { email, firebase_password } = payload;
            try {
                const auth = getAuth(fb);
                const { user } = await signInWithEmailAndPassword(auth, email, firebase_password);
                this.subscribed = true;
                window.addEventListener('online', () => {
                    getDoc(doc(db, 'users', user.uid)).then(argUser => {
                        return this.dispatch('processFirebaseUpdates', argUser);
                    });
                });
                context.commit('updateFirebaseStatus', true);
                onSnapshot(doc(db, 'users', user.uid), argUser => {
                    this.dispatch('processFirebaseUpdates', argUser);
                });

                onSnapshot(doc(db, 'app', 'meta'), snapshot => {
                    const lastTimestamp = context.state.lastJSUpdate;
                    const { js_updated_at } = snapshot.data();
                    if (!lastTimestamp) {
                        return context.commit('updateJSTimestamp', js_updated_at);
                    }
                    if (lastTimestamp < js_updated_at) {
                        context.dispatch(
                            'notifyWarning',
                            {
                                message: `${i18n.t(
                                    'webAppUpdated'
                                )} <a href="#" onclick="window.location.reload(true); return false">${i18n.t(
                                    'webAppUpdatedReload'
                                )}</a>.
                                    ${i18n.t('viewChangeLog')}
                                    <a href="https://help.ludum.com/en/collections/2753022-ludum-feature-updates" target="_blank">
                                        ${i18n.t('here')}
                                    </a>.
                                `,
                                persist: true
                            },
                            { root: true }
                        );
                    }
                });
            } catch (error) {
                context.commit('updateFirebaseStatus', false);
            }
        }
    }
};

export default user;
